import type { DynamicTableConfig, Page } from '../../types';
import { REPORTING_PERIOD_LABEL_TOKEN } from '../../../utils/reporting-period';

const scsAppointmentColumns: DynamicTableConfig['columns'] = [
  { key: 'scs1', label: 'SCS 1' },
  { key: 'scs2', label: 'SCS 2' },
  { key: 'gradeTotal', label: 'Grade total', calculation: 'rowSum', enabled: false },
];

const buildDynamicAppointmentTable = (rows: DynamicTableConfig['rows']): DynamicTableConfig => ({
  rowHeader: 'Grade',
  columns: scsAppointmentColumns,
  rows,
});

const scsAppointments: Page[] = [
  {
    pageType: 'question',
    title: 'SCS appointments',
    name: 'scs-appointments-page',
    hidePageTitle: true,
    hideContinueButton: true,
    navigateToSummaryOnComplete: true,
    questions: [
      {
        questionType: 'dynamicTable',
        title: 'SCS interim appointments',
        name: 'scs-interim-appointments',
        dynamicTable: buildDynamicAppointmentTable([
          {
            key: 'interimPostholders',
            label:
              '#1 As of the start of this reporting quarter, how many substantive posts are currently held by an interim postholder?',
          },
          {
            key: 'interimSixToTwelveMonths',
            label:
              '#2 Of the interim postholders listed in response to #1, how many are expected to have a total appointment duration of between 6 and 12 months?',
            lessThanOrEqualToRow: 'interimPostholders',
          },
          {
            key: 'interimMoreThanTwelveMonths',
            label:
              '#3 Of the interim postholders listed in response to #1, how many are expected to have a total appointment duration of more than 12 months?',
            lessThanOrEqualToRow: 'interimPostholders',
            setQuestionWhenRowTotalEquals: {
              value: 0,
              targetQuestion: 'interim-more-than-twelve-months-detail',
              targetValue: 'N/a',
            },
          },
        ]),
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title:
          '#4 For the interim postholders referenced in #3, please provide any explanatory detail for the expected length of post',
        name: 'interim-more-than-twelve-months-detail',
        hint: 'Please enter "N/a" if there were no relevant appointment',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'dynamicTable',
        title: 'Substantive appointments',
        name: 'substantive-appointments',
        dynamicTable: buildDynamicAppointmentTable([
          {
            key: 'externallyAdvertisedAppointments',
            label:
              '#5 How many permanent appointments externally advertised were made to SCS grades in this reporting quarter',
            hint: `Current reporting period: ${REPORTING_PERIOD_LABEL_TOKEN}`,
          },
          {
            key: 'previouslyInterimAppointments',
            label:
              '#6 Of the appointments listed in #5, how many of the appointed candidates had previously held the role on an interim basis?',
            lessThanOrEqualToRow: 'externallyAdvertisedAppointments',
          },
          {
            key: 'appointedAfterSixToTwelveMonths',
            label:
              '#7 Of the interim postholders who were later substantively appointed as listed in #6, how many had held the role for between 6 and 12 months?',
            lessThanOrEqualToRow: 'previouslyInterimAppointments',
          },
          {
            key: 'appointedAfterMoreThanTwelveMonths',
            label:
              '#8 Of the interim postholders later substantively appointed as listed in #6, how many had held the role for more than 12 months?',
            lessThanOrEqualToRow: 'previouslyInterimAppointments',
            setQuestionWhenRowTotalEquals: {
              value: 0,
              targetQuestion: 'appointed-more-than-twelve-months-detail',
              targetValue: 'N/a',
            },
          },
        ]),
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title:
          '#9 For the interim postholders referenced in #8, please provide any explanatory detail for the length of the interim post.',
        name: 'appointed-more-than-twelve-months-detail',
        hint: 'Please enter "N/a" if there were no relevant appointment',
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default scsAppointments;
